import _ from 'lodash';

import createModel from './lib/create_model';

const SOURCES = ['roleFeatures', 'gatekeeperFeatures', 'browserCapabilities'];
// TODO: remove ignore sources loaded once we get proper blocking on loading the app features for all sources.
const IGNORE_SOURCES_LOADED = ['demoMode'];

export const FeatureList = createModel({
  modelName: 'FeatureList',

  properties: {
    /* FEATURE FLAGS */

    // gates the per-channel "Customize Sounds and Colors" section on the agent
    // profile (per-channel notification sounds and per-channel bubble border colors)
    agentChannelCustomize: Boolean,

    // Gates the sagan-admin Browsing Engagement admin page for configuring
    // per-org Beacon rate limits
    browsingEngagement: Boolean,

    // Enables new Chat V2 enablement option in Admin experience
    chatV2: Boolean,

    // Enables rich text formatting (bold/italic/underline/links) in the chat composer
    chatRichTextFormatting: Boolean,

    // enables Customer.io analytics embed
    customerio: Boolean,

    // Enables "Contact Support" menu link for the users with correct roles
    contactSupport: Boolean,

    // enables the Conversational Analytics chat panel in Reports
    conversationalAnalytics: Boolean,

    // gates the client-side filter bar on the customer conversation-history
    // timeline (hide item-type groups, filter by endpoint address)
    conversationHistoryFilter: Boolean,

    // enables the Liveboard tiles for custom channels
    customChannelsLiveboard: Boolean,

    debug: Boolean,

    // enable dedicated hero capabilities
    dedicatedHero: Boolean,

    demoMode: Boolean,

    // can accept/make phones calls over direct dial
    directDial: Boolean,

    // disable analytics tracking (e.g. segment), typically used for non-production orgs
    disableAnalytics: Boolean,

    // disable Keyword Groups in admin part
    disableKeywordGroup: Boolean,

    // suppress new version notifications
    disableNewVersionNotifications: Boolean,

    // show a global yellow banner for overdue payment messaging
    paymentOverdueBanner: Boolean,

    // gates the granular People-section admin permissions (users/teams/roles) in
    // the admin app; when off, the People section keeps its legacy role-name gates
    peopleAdminPermissions: Boolean,

    // overwrites multi-source-customer-lookup display behavior to use customer-profile-def to display externalCustomerId
    displayLegacyExternalId: Boolean,

    doubleInboxPollingInterval1: Boolean,
    doubleInboxPollingInterval2: Boolean,

    // Gates the V2 chat-widget Beacon manifest / MCP integrations admin UI
    enableBeacon: Boolean,

    // Gates the Chat Launchers admin UI for the V2 chat widget
    enableChatLaunchers: Boolean,

    // Enable Edit and Add HTTP2 Node in IVR Editor
    enableEditingHttp2Node: Boolean,

    // Enable adding the SIP Refer node in IVR Editor
    enableAddingSipReferNode: Boolean,

    // Enable adding the SIP Forward node in IVR Editor
    enableAddingSipForwardNode: Boolean,

    // enables granular topic requirements (per-group, category-only topics) for conversation closure
    enhancedTopicWorkflows: Boolean,

    // use HTML to generate plain text
    emailPlainTextFromHtml: Boolean,

    // Enable HeroAI tools in the new Email composer
    enableEmailComposerHeroAI: Boolean,

    // enable Gemini conversational analytics in Looker embeds
    geminiInLooker: Boolean,

    // enable "fake webRTC" channel to prevent browser throttling
    enableFakeWebRTC: Boolean,

    // ranks favorited answers above other hits in answer panel search results
    favoriteAnswersFirstInSearch: Boolean,

    // forces the email composer to be the new slate composer
    forceEmailSlateBeta: Boolean,

    // mounts DeviceProvider so the post-auth phone shell (mobile layouts,
    // [data-device='phone'] styles) activates. Pre-auth pages have their own
    // width-based responsive styles and don't depend on this flag.
    frontendPhoneSupport: Boolean,

    // makes the side panel extend to full page height, moving the footer under the conversation feed only
    fullHeightSidePanel: Boolean,

    // lets agents drag the customer side panel wider and persists the width on
    // agent preferences; also switches the conversation feed's autosizer from a
    // throttle to per-frame coalescing so the feed keeps up with the drag
    sidePanelResize: Boolean,

    // gates i18n UI localization: enables language detection so the interface
    // renders in the agent's chosen/system language instead of always English
    // (see presentation/components/i18n_feature_gate.jsx)
    i18n: Boolean,

    // enables variable interpolation in URL links in the answer editor
    interpolateVariablesInURL: Boolean,

    // allows user creation  on first login attempt using SSO
    allowJitUserCreation: Boolean,

    // gates client-side filtering and expanded sorting on the agents liveboard
    // (filter by channel/status/focus; sort by status, conversation count, time in status)
    liveboardAgentFilters: Boolean,

    // keys the agents liveboard "No agent reply" dot on the reply SLA due time
    // instead of the absence of a fulfilled time
    liveboardReplySlaDot: Boolean,

    // new property 'loop the queue and hold music' on the voice editor page
    loopHoldMusic: Boolean,

    // mask the routing counts in the agent status button if the user does not have liveboards permission
    maskRoutingCounts: Boolean,

    // whether the browser supports and the user allowed the microphone
    microphone: Boolean,

    // Activate UI to allow for configuration of SMS endpoints to be controlled by message automation.
    smsMessageAutomation: Boolean,

    // Gates Sidekick for Sales chat-related UI work
    sidekickSalesChatV2: Boolean,

    // can accept/make phone calls over SIP
    sip: Boolean,

    // new version of slate
    slateBeta: Boolean,

    // can send/receive sms messages
    sms: Boolean,

    // enable warning on email conversation items when spam is detected
    spamEmailWarning: Boolean,

    // enable spam rule condition
    spamRuleCondition: Boolean,

    // enables UI controls that trigger AI Suggested Replies features
    suggestedReplies: Boolean,

    // enables tasks search in the search results
    tasksSearch: Boolean,

    // enables CTI integration with third party telephony systems
    telephonyApi: Boolean,

    // enables team-level messaging capacity configuration
    teamMessagingCapacity: Boolean,

    // enables inline video previews (and in-modal playback) for video message
    // attachments across channels; when off, videos render as generic file cards
    videoAttachmentPreview: Boolean,

    // enables the Team Assist AI copilot panel
    teamAssistAI: Boolean,

    // testFlag is used only for testing, as feature flags should generally be short-lived
    testFlag: Boolean,

    // enables editing topics and custom attributes on tasks (pills in the task
    // item and the task comment panel)
    topicsOnTasks: Boolean,

    // Enable endpoint cache in telephony service
    useEndpointCache: Boolean,

    // enables user survey on away page
    userSurvey: Boolean,

    // can accept/make phones calls over webRTC
    webRTC: Boolean,

    // sets the Twilio Voice device logLevel to DEBUG for verbose telephony logging
    twilioDebugLogging: Boolean,

    // Gates the new main-nav sidebar layout (Home / Manage / Analyze / Test / Deploy)
    newMainNav: Boolean,

    // Rollout flag for the glados self-serve onboarding home. When on for an
    // eligible org (see shopifySourcedDeal / smbSelfServe), admins are routed to
    // /sidekick/getting-started on login instead of communication-sessions.
    // See RequestOrganization.redirectSelfServeOnboardingOrg.
    onboardingHome: Boolean,

    // Marks an org that came in through the Shopify marketplace form (set by
    // ps-org-creator for source === 'shopify'). Eligibility signal for the
    // onboarding home, independent of the onboardingHome rollout flag.
    shopifySourcedDeal: Boolean,

    // Marks a manually-inked SMB self-serve deal that did not come through the
    // Shopify app store (set per-org in org manager). Eligibility signal for the
    // onboarding home, alongside shopifySourcedDeal.
    smbSelfServe: Boolean,

    // adjust keepalive time for MQTT client
    doubleKeepAlive1: Boolean,
    doubleKeepAlive2: Boolean,
    tripleKeepAlive: Boolean,

    // adjust reconnect period for MQTT client
    doubleReconnectPeriod: Boolean,
    tripleReconnectPeriod: Boolean,

    /* ROLE FEATURES */

    // - can navigate to admin settings
    adminSettings: Boolean,

    // allows to update agent status
    agentStatusControl: Boolean,

    // can create and update campaigns
    campaignsManagement: Boolean,

    // can create and update channels
    channelManagement: Boolean,

    // can create & update custom attributes
    customAttributeManagement: Boolean,

    // can edit assignee (dedicated hero) for customer
    'customer:dedicatedHero': Boolean,

    // can create and update email domains
    emailDomainsManagement: Boolean,

    // can delete communication attachments
    manageAttachments: Boolean,

    // can download and delete phone call and voicemail recordings
    manageVoiceRecordings: Boolean,

    // can view and control Mobile Phone Number Identification
    mobileNumberLookupManagement: Boolean,

    // can view all group inboxes (in the dashboard)
    'inboxes:viewAll': Boolean,

    // - can create and edit routing groups
    // - can assign agents to routing groups
    inboxManagement: Boolean,

    // - can list, add, delete personal API tokens
    // - can list, add, delete webhooks
    devManagement: Boolean,

    // can view developer debugging tools
    debuggingView: Boolean,

    // - can delete profile
    // - can redact conversation item
    complianceManagement: Boolean,

    // can view and edit integrations (eg. customer lookup)
    integrationsManagement: Boolean,

    // can view and edit IVR configuration
    ivrConfigurationManagement: Boolean,

    // can listen to live calls
    'conversation:callCoach': Boolean,

    // can listen to call recordings
    'conversation:callRecordingListen': Boolean,

    // can manage conversations (eg. profiles, tasks, notes, search)
    // - can be routed conversations
    // - can work on conversations (task, notes, etc.)
    // - can search across all conversations
    'conversation:general': Boolean,

    // can send outbound communications
    'conversation:outbound': Boolean,

    // can view conversations
    'conversation:read': Boolean,

    // can moderate social comments
    'conversation:socialCommentModeration': Boolean,

    // can edit answers/variables
    'answers:admin': Boolean,

    // can read answers
    'answers:read': Boolean,

    // can view the audit log
    'auditLog:read': Boolean,

    // can configure agent experience settings
    'agentExperience:admin': Boolean,

    // can configure communication channels
    'channels:admin': Boolean,

    // can configure routing and orchestration settings
    'orchestration:admin': Boolean,

    // can manage proactive conversation campaigns
    'proactiveConversations:admin': Boolean,

    // can view the agent directory (People > Users)
    'users:read': Boolean,

    // can create, edit, and deactivate users
    'users:admin': Boolean,

    // can create, edit, and delete teams
    'teams:admin': Boolean,

    // can view, create, edit, and archive custom roles
    'roles:admin': Boolean,

    // standalone login permission
    login: Boolean,

    // can build reports
    reportBuilder: Boolean,

    // can manage roles that can be assigned to users
    roleManagement: Boolean,

    // can edit rules
    rulesManagement: Boolean,

    // add support for flexible cards in the admin
    adminFlexibleCardEnabled: Boolean,

    // enable access to sidekick conversation UI
    sidekickConversationManagement: Boolean,

    // gates the Signals nav item; its glados destination route 404s without it
    sentinel: Boolean,

    // gates the Session Explorer nav item for non-Sagan-admin viewers; its
    // glados destination route 404s without it
    sessionExplorerForAgents: Boolean,

    // gates the Knowledge Sources nav item; its glados destination route 404s without it
    knowledgeUI: Boolean,

    // can edit skills
    skillManagement: Boolean,

    // whether the user has logged in using SSO
    sso: Boolean,

    // can edit single sign-on configuration
    ssoConfigurationManagement: Boolean,

    // can view the existing users
    // can edit teams
    teamManagement: Boolean,

    // can create & update topics
    'topics:admin': Boolean,

    // userManagement enables the following:
    // - can create and edit users (agents only)
    // - can trigger password reset for users (agents only)
    // - can edit agent channels
    userManagement: Boolean,

    // - can navigate to answer performance page
    viewAnswerPerformance: Boolean,

    // - can navigate to guide performance page
    viewGuidePerformance: Boolean,

    // can view liveboards
    viewLiveboards: Boolean,

    // can view reports
    'reports:read': Boolean,

    // can view webhooks admin page
    webhooks: Boolean,

    // in-app notification For Answer Threads Classroom
    atClassroomNotification: Boolean,

    // receives in-app notifications for Sidekick Customers
    sidekickNotifications: Boolean,

    // use topic IDs selector in Sidekick Threads in AddTopics action if possible
    sidekickThreadUseTopicIds: Boolean,

    // can view the App Actions UI
    showLasagnaPage: Boolean,
  },
});

const AppFeatures = createModel({
  modelName: 'AppFeatures',

  properties: {
    roleFeatures: FeatureList,
    gatekeeperFeatures: FeatureList,
    browserCapabilities: FeatureList,
  },

  replace(source, features) {
    const featureList = new FeatureList(features);
    const newAttrs = _.merge({}, this, { [source]: featureList });
    return new AppFeatures(newAttrs);
  },

  sourcesAreLoaded() {
    return _.chain(this).pick(SOURCES).values().every().value();
  },

  isEnabled(feature) {
    if (this.sourcesAreLoaded() || _.includes(IGNORE_SOURCES_LOADED, feature)) {
      return (
        _.some(SOURCES, source => this[source] && this[source][feature] === true) &&
        _.every(SOURCES, source => this[source] && this[source][feature] !== false)
      );
    }

    return false;
  },

  statics: {
    create(attrs) {
      return new this(attrs);
    },
    SOURCES,
  },
});

export default AppFeatures;
